import { MutableRefObject, useCallback, useEffect, useRef } from 'react';
import { BehaviorSubject, from, of, Subject } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { CategoriesApi, CategoryId } from 'api/timesheets/categories';
import { Result } from 'core/result';
import { logger } from 'services';
import { Categories } from './core';
import { deleted, deleting, undeleted } from './deleting';

const NO_OP = () => {};

export const useRemove = (
  categories$: MutableRefObject<BehaviorSubject<Categories>>,
  initialized: boolean,
): ((id: CategoryId) => void) => {
  const delete$ = useRef(new Subject<CategoryId>());
  const remove = useCallback(
    (id: CategoryId) => delete$.current.next(id),
    [delete$],
  );

  useEffect(() => {
    if (!initialized) {
      return NO_OP;
    }

    const subscription = delete$.current
      .pipe(
        tap((id) =>
          categories$.current.next(deleting(id)(categories$.current.value)),
        ),
        mergeMap((id) =>
          from(CategoriesApi.remove(id)).pipe(
            map(Result.ok),
            catchError((error: Error) => of(Result.error(error))),
            map((result) => [id, result] as [CategoryId, Result<void, Error>]),
          ),
        ),
      )
      .subscribe(([id, result]) =>
        Result.on(
          () =>
            categories$.current.next(deleted(id)(categories$.current.value)),
          (error: Error) => {
            const categories = categories$.current.value;

            logger.error('failed to delete category', {
              error,
              categories,
              id,
            });
            categories$.current.next(undeleted(id)(categories));
          },
        )(result),
      );

    return () => subscription.unsubscribe();
  }, [initialized, categories$, delete$]);

  return remove;
};
